import { Country } from '$/lib/Address';

/**
 * Service IDs in the order they should appear in the report.
 */
export enum Services {
	CreditReport               = 18,
	CriminalCheck              = 161,
	PreviousAddressHistory     = 166,
	TelecheckCheckVerification = 8,
	NationwideEvictionSearch   = 162,
	SexOffenderSearch          = 163,
	OFACFederalSearch          = 165,
}

export enum TenantAlertPackage {
	Basic = 'basic',
	Pro   = 'pro',
}

export enum ReportStatus {
	Pending   = 'Pending',
	Completed = 'Completed',
}

export enum Action {
	NewApplication = 'NewApplication',
	GetApplication = 'GetApplication',
}

export enum AddressType {
	Standard = 'standard',
	PO       = 'po',
	Rural    = 'rural',
}

export enum TenantAlertAPIStatus {
	Ok    = 'OK',
	Error = 'ERROR',
}

export enum ApplicationStatus {
	Processing = 'Processing',
	Completed  = 'Completed',
	Error      = 'ERROR'
}

export enum SampleReportType {
	TenantAlertBasic = 'tenant-alert-basic',
	TenantAlertPro   = 'tenant-alert-pro',
}

export class TenantAlertReport {

	error?: string;
	sections: TenantAlertReportSection[];

	constructor(errorOrSections: string | TenantAlertReportSection[] = []) {
		if (typeof errorOrSections === 'string') {
			this.error    = errorOrSections;
			this.sections = [];
		}
		else {
			this.sections = errorOrSections;
		}
	}

}

export interface TenantAlertReportSection {
	status:                       ReportStatus;
	expirationDate?:              string;
	needAgreeDisclaimerCriminal?: boolean;
	serviceId:                    number;
	serviceName:                  string;
	html:                         string;
	id?:                          number;
	warnings?:                    string;
}

export interface TenantAlertApplicant {
	firstName:    string;
	middleName?:  string;
	lastName:     string;
	suffix?:      string;
	email?:       string;
	dateOfBirth:  Date;
	nationalID:   string;
	phoneNumber?: string;
	address: {
		unitNumber?: string;
		street:      string;	// street numbers can only be numbers/hyphen, street direction one of N,E,S,W,NE,SE,SW,NW
		city:        string;
		province:    string;
		postalCode:  string;
		country:     Country.US; // TenantAlert is currently only used in the US so we can hardcode the country
	};
	extendedFields?: {
		limelyteDriversLicense: {
			driversLicenseNumber: string;
			licenseState:         string;
		};
	};
}

export interface Applicant {
	fname:   string;
	mname?:  string;
	lname:   string;
	suffix?: string;
	email:   string;
	dob:     string;
	ssn:     string;
	guid?:   string;
	phone:   string;
	address: {
		addressType: AddressType;
		address:     string;
		suite?:      string;
		city:        string;
		state:       string;
		zip:         string;
	};
	extendedFields?: {
		limelyteDriversLicense: {
			driversLicenseNumber: string;
			licenseState:         string;
		};
	};
}

export interface NewApplicationRequest {
	xml: {
		action: Action.NewApplication;
		request: {
			endUserName: string;
			services?: {
				service: number;
			};
			packageId?: number;           // send a package_id or service, not both
			locationId: string;
			applicant:  Applicant;
		};
	};
}

export interface NewApplicationResponse {
	status: TenantAlertAPIStatus;
	result: {
		applicationId: string;
	};
	error?: string;
}

export interface GetApplicationRequest {
	xml: {
		action: Action.GetApplication;
		request?: {
			responseFormat: string;
			applicationId:  string;
		};
	};
}

export interface GetApplicationResponse {
	status: TenantAlertAPIStatus;
	result: {
		application:              {
			id:        string;
			timestamp: string;
			status:    ApplicationStatus;
			isPaid:    boolean;
		};
		applicantFilledFormsHtml: string;
		applicantHtml:            string;
		applicants:               Applicant;
		report:                   PossibleArray<TenantAlertReportSection>;
		summary: string;
		scores: {
			value:  number;
			source: string;
			name:   string;
		}[];
		decisionResults: {
			result:   boolean;
			score:    number;
			possible: number;
			decision: string;
			reason:   string;
		};
	};
}

/**
 * These are the phone numbers that can be used to test various scenarios with the TenantAlert API.
 */
export enum TestResponsePhoneNumbers {
	NoData      = '+15555555555',   // N/A
	Approved    = '+15555550011',   // no records found / Credit Score of 739 Approved
	Declined    = '+15555550012',   // all records found / Credit Score of 524 Declined
	Conditional = '+15555550013',   // only have path, ssn, blj records / Credit Score of 650 Approved with Conditions
	Error       = '+15555550014',   // Null / error with reports
	Frozen      = '+15555550015',   // Null / Frozen Credit example
}

export const TenantAlertTestUsers: Dictionary<Dictionary<TenantAlertApplicant>> = {
	transUnionUS : {
		JohnLamb : {
			firstName   : 'John',
			lastName    : 'Lamb',
			nationalID  : '223-13-1456',
			dateOfBirth : new Date('1971-02-19'),
			address     : {
				street     : '912 Main St',
				city       : 'Fantasy Island',
				province   : 'IL',
				postalCode : '60750',
				country    : Country.US,
			},
		},
		KathleenDald : {
			firstName   : 'Kathleen',
			lastName    : 'Dald',
			nationalID  : '305-01-3831',
			dateOfBirth : new Date('1964-03-09'),
			address     : {
				street     : '7942 W State St',
				city       : 'Fantasy Island',
				province   : 'IL',
				postalCode : '60750',
				country    : Country.US,
			},
		},
		WilliamEaston : {
			firstName   : 'William',
			lastName    : 'Easton',
			nationalID  : '304-12-8712',
			dateOfBirth : new Date('1980-05-25'),
			address     : {
				street     : '2010 W 80th St',
				city       : 'Fantasy Island',
				province   : 'IL',
				postalCode : '60750',
				country    : Country.US,
			},
		},
	},
	equifaxUS : {
		SallyPublic : {
			firstName   : 'Sally',
			lastName    : 'Public',
			nationalID  : '306-30-2348',
			dateOfBirth : new Date('1943-07-04'),
			address     : {
				street     : '123 First St',
				city       : 'Oz',
				province   : 'GA',
				postalCode : '00999',
				country    : Country.US,
			},
		},
		ConnieCotton : {
			firstName   : 'Connie',
			lastName    : 'Cotton',
			nationalID  : '565-85-1257',
			dateOfBirth : new Date('1983-08-11'),
			address     : {
				street     : '469 San Bernardo St',
				city       : 'Portland',
				province   : 'OR',
				postalCode : '97201',
				country    : Country.US,
			},
		},
		MagnoliaLea : {
			firstName   : 'Magnolia',
			lastName    : 'Lea',
			nationalID  : '518-92-3248',
			dateOfBirth : new Date('1953-09-24'),
			address     : {
				street     : '7321 Delomites Ln',
				city       : 'Colombus',
				province   : 'OH',
				postalCode : '43216',
				country    : Country.US,
			},
		},
		JonathanConsumer : {
			firstName   : 'Jonathan',
			lastName    : 'Consumer',
			nationalID  : '999-99-9999',
			dateOfBirth : new Date('1970-01-01'),
			address     : {
				street     : '360 Nothere street',
				city       : 'San Bernardino',
				province   : 'CA',
				postalCode : '65432',
				country    : Country.US,
			},
		},
	},
};
